import React, {useState, FunctionComponent, useEffect} from "react"
import {useForm} from "react-hook-form"
import Button from "@murphy-frontend/web-core/components/Button"
import {isUSPhoneNumber} from "@murphy-frontend/common/utils"
import {yupResolver} from "@hookform/resolvers/yup"
import * as yup from "yup"
import TextInput from "./TextInput"
import RadioInput from "./RadioInput"
import CheckboxInput from "./CheckboxInput"
import {Constant} from "@murphy-frontend/web-core/models/Constant"
import {rolesUser} from "@murphy-frontend/web-core/constants"
import {updateUserMutation} from "@murphy-frontend/common/api/Users/Mutations"
import {UpdateUserRequest} from "@murphy-frontend/common/interfaces/IUsersApi"

export interface UserFormViewModel {
	id: string
	username: string
	email: string
	role: string
	phonenumber: string
	termsAgreed: boolean
	modules: string[]
}

interface UserData {
	CustomerUserId: string
	Permissions?: Number[]
	Role: number
	Username: string
	Email: string
	Phonenumber?: string
}

interface EditUserModalProps {
	closeModal: () => void
	translations: Record<any, any>
	products: Constant[]
	userData: UserData
}

const EditUserModal: FunctionComponent<EditUserModalProps> = ({closeModal, translations, products, userData}) => {
	const [loading, setIsLoading] = useState(false)

const phoneRegExp = /^\+[(]?[0-9]{1,4}[)]?[-.\s]?[0-9]{5,12}$/

	const userSchema = yup.object().shape(
		{
			username: yup.string().required(translations.userNameReq),
			email: yup.string().required(translations.emailReq).email(translations.emailInvalid),
			phonenumber: yup
				.string()
				.nullable()
				.notRequired()
				.when("phonenumber", {
					is: (value: string) => value?.length > 0, // Only apply validation if a phone number is provided
					then: yup.string().test({
						name: "usPhoneNumberTerms",
						message: translations.theTermsUS,
						test: function _(value) {
							const {termsAgreed} = this.parent
							console.log(value.match(phoneRegExp))
							if (!Array.isArray(value.match(phoneRegExp))) {
								console.log(value.match(phoneRegExp))
								return this.createError({
									path: "phonenumber",
									message: translations.phoneInvalid
								})
							}
							if (isUSPhoneNumber(value) && !termsAgreed) {
								return this.createError({
									path: "termsAgreed",
									message: translations.termsOfAgreementUS
								})
							}
							return true
						}
					}),
					otherwise: yup.string().nullable() // Make sure it's not required if no value is present
				}),
			role: yup
				.string()
				.required()
				.oneOf(rolesUser.map(role => role.value)),
			modules: yup.array().nullable().notRequired(),
			termsAgreed: yup.boolean().default(false)
		},
		[["phonenumber", "phonenumber"]]
	)

	const {
		register,
		formState: {errors, dirtyFields, isDirty},
		handleSubmit,
		watch
	} = useForm<UserFormViewModel>({
		resolver: yupResolver(userSchema),
		defaultValues: {
			username: userData.Username,
			email: userData.Email,
			phonenumber: userData.Phonenumber || "",
			termsAgreed: false,
			role: rolesUser.find(role => role.id === userData.Role).value,
			modules: userData.Permissions ? userData.Permissions.map(mod => mod.toString()) : []
		}
	})

	const {mutate: updateUserMut, isPending: isLoading} = updateUserMutation(userData.CustomerUserId, translations)

	const onSubmitHandler = (updateData: UserFormViewModel) => {
		const allDirtyFields = Object.entries(dirtyFields)
			.filter(([key, value]) => value === true)
			.map(item => item[0])
		const updateDataToSubmit: any = {}

		Object.entries(updateData).forEach(([key, value]) => {
			const fieldIsDirty = allDirtyFields.includes(key)
			if (fieldIsDirty) {
				updateDataToSubmit[key] = value
			}
		})

		if (updateDataToSubmit) {
			let updateUserRequest: UpdateUserRequest = {}

			if (updateData.modules.length) {
				updateUserRequest.UpdatedProductsList = updateData.modules.map(val => parseInt(val))
			}

			if (updateData.phonenumber) {
				updateUserRequest.NewMobilePhone = updateData.phonenumber
			}

			if (updateData.username) {
				updateUserRequest.NewUserName = updateData.username
			}

			if (updateData.role) {
				updateUserRequest.NewRole = rolesUser.find(role => role.value === updateData.role).id
			}

			if (Object.keys(updateUserRequest).length > 0) {
				setIsLoading(true)
				updateUserMut(updateUserRequest, {
					onSuccess: () => {
						setIsLoading(false)
						closeModal()
					},
					onSettled: () => setIsLoading(false)
				})
			}
		}
	}

	const isRequiredField = (field: string) => {
		const tests = userSchema.describe().fields[field]?.tests

		return tests?.[0]?.name === "required"
	}

	const phoneNumber = watch("phonenumber")
	const isUSPhone = isUSPhoneNumber(phoneNumber)

	return (
		<div className='add-user-form-container'>
			<section>
				<h2>Edit User Profile</h2>
			</section>
			<form onSubmit={handleSubmit(onSubmitHandler)}>
				<TextInput
					register={register}
					name='username'
					title={translations["lang-table-username"]}
					disabled={false}
					error={errors.username}
					required={isRequiredField("username")}
				/>
				<TextInput
					register={register}
					name='email'
					title={translations["lang-label-email"]}
					disabled={true}
					error={errors.email}
					required={isRequiredField("email")}
				/>
				<TextInput
					register={register}
					name='phonenumber'
					title={translations.phonenumber}
					disabled={false}
					error={errors.phonenumber}
					required={isRequiredField("phonenumber")}
				/>
				<div className='row field-add-user'>
					<div className='field-row'>
						<div className='field-input-wrapper'>
							<div className='field-label-wrapper'>
								<div className='field-label-radio'>{translations["lang-label-role"]}</div>
								{isRequiredField("role") && <div className='field-required'>Required</div>}
							</div>
							<div className='field-input-grid-wrapper'>
								<div className='field-input-wrapper'>
									{rolesUser.map(({value, id, translationKey}) => (
										<RadioInput
											key={id}
											register={register}
											name='role'
											title={translations[translationKey]}
											value={value}
											disabled={false}
										/>
									))}
								</div>
								{errors.role && <p className='field-error'>{errors.role.message}</p>}
							</div>
						</div>
					</div>
				</div>
				<div className='row field-add-user'>
					<div className='field-row'>
						<div className='field-input-wrapper'>
							<div className='field-label-wrapper'>
								<div className='field-label-radio'>{translations.modulesAccess}</div>
								{isRequiredField("modules") && <div className='field-required'>Required</div>}
							</div>
							<div className='field-input-grid-wrapper'>
								<div className='field-checkbox-grid'>
									{products.map(({value, id}) => (
										<CheckboxInput
											key={id}
											value={id.toString()}
											title={value}
											register={register}
											name='modules'
											disabled={false}
										/>
									))}
								</div>
								{errors.modules && <p className='field-error'>{errors.modules.message}</p>}
							</div>
						</div>
					</div>
				</div>
				{isUSPhone && (
					<div className='row field-add-user'>
						<div className='field-row'>
							<div className='field-input-wrapper field-terms'>
								<div className='field-input-grid-wrapper'>
									<label htmlFor='termsAgreed' className='field-terms-label'>
										{translations.youMustAgree}
										<br /> {translations.agreeToTheTerms}
									</label>
									<input
										className='field-terms-checkbox'
										type='checkbox'
										id='termsAgreed'
										name='termsAgreed'
										disabled={false}
										{...register("termsAgreed")}
									/>
									{errors.termsAgreed && <p className='field-error'>{errors.termsAgreed.message}</p>}
								</div>
							</div>
						</div>
					</div>
				)}
				<div className='row field-add-user-btns'>
					<Button
						buttonStyle={"btn--outline"}
						isNeutralButton={true}
						buttonSize={"btn--medium"}
						onClick={closeModal}
					>
						{translations.cancel}
					</Button>
					<Button type='submit' buttonSize={"btn--medium"} isLoading={loading}>
						{translations.updateUser}
					</Button>
				</div>
			</form>
		</div>
	)
}

export default EditUserModal
